import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, BookOpen, Calendar, Radio, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceTypes = [
  { value: 'teacher', label: 'Sikh Teacher / Educator', icon: Users, description: 'Teach Gurbani, Kirtan, Gurmukhi, or Sikh history' },
  { value: 'katha', label: 'Katha Speaker', icon: BookOpen, description: 'Share spiritual discourses and teachings' },
  { value: 'event', label: 'Event Organizer', icon: Calendar, description: 'Organize community events and gatherings' },
  { value: 'live_stream', label: 'Live Stream Provider', icon: Radio, description: 'Provide live kirtan from your Gurudwara' },
];

const CollaboratePage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    service_type: '',
    name: '',
    email: '',
    phone: '',
    description: '',
    expertise: '',
    location: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...form,
      expertise: form.expertise ? form.expertise.split(',').map(s => s.trim()).filter(Boolean) : []
    };

    try {
      await axios.post(`${API_URL}/service-requests`, payload);
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream-100 py-16" data-testid="collaborate-success">
        <div className="max-w-lg mx-auto px-4">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Thank You for Your Interest!
              </h2>
              <p className="text-gray-600 mb-6">
                Your collaboration request has been submitted. Our team will review it and get back to you soon.
              </p>
              <p className="text-sm text-gray-500">
                For questions, contact us at:<br />
                <a href="mailto:Khalsasidak13@gmail.com" className="text-saffron-600">Khalsasidak13@gmail.com</a>
              </p>
              <Button
                className="mt-6 btn-primary"
                onClick={() => { setSubmitted(false); setForm({ service_type: '', name: '', email: '', phone: '', description: '', expertise: '', location: '' }); }}
              >
                Submit Another Request
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="collaborate-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Collaborate With Us
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join the Khalsa Sidak community! Share your services, teach, organize events, 
            or provide live kirtan streams for our global Sikh community.
          </p>
        </div>

        {/* Service Types */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {serviceTypes.map((type) => (
            <Card
              key={type.value}
              className={`cursor-pointer transition-all ${
                form.service_type === type.value
                  ? 'ring-2 ring-saffron-500 border-saffron-500'
                  : 'hover:border-saffron-200'
              }`}
              onClick={() => setForm({ ...form, service_type: type.value })}
              data-testid={`service-type-${type.value}`}
            >
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  form.service_type === type.value ? 'bg-saffron-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <type.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{type.label}</h3>
                  <p className="text-sm text-gray-500">{type.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Your Information</CardTitle>
            <CardDescription>Tell us about yourself and how you'd like to contribute</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    data-testid="collab-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    data-testid="collab-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="240-xxx-xxxx"
                    data-testid="collab-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="City, State"
                    data-testid="collab-location"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="expertise">Areas of Expertise (comma separated)</Label>
                <Input
                  id="expertise"
                  value={form.expertise}
                  onChange={(e) => setForm({ ...form, expertise: e.target.value })}
                  placeholder="e.g., Kirtan, Katha, Gurmukhi, Sikh History"
                  data-testid="collab-expertise"
                />
              </div>

              <div>
                <Label htmlFor="description">Tell Us About Yourself *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe your background, experience, and how you'd like to contribute to the community..."
                  rows={4}
                  required
                  data-testid="collab-description"
                />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={!form.service_type || loading}
                  data-testid="collab-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Collaboration Request'
                  )}
                </Button>
              </div>

              <p className="text-center text-sm text-gray-500">
                By submitting, you agree to be contacted by our team regarding your request.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CollaboratePage;
